import React, { useState, useEffect, useMemo, useRef } from "react";
import "./ShopPage.css";
import Footer from "../../components/Footer";
import ShopCard from "../../components/ShopCard/ShopCard";
import noConversionBanner from "../../assets/IMG_4638.png";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import productsData from "../../data/products";
import { getProductPrices, getPriceForSku } from "../../api/productPriceApi";
import SEOHead from "../../seo/SEOHead";
import { PAGE_SEO } from "../../seo/seoConfig";
import { breadcrumbSchema } from "../../seo/structuredData";
import {
  Flame,
  CheckCircle2,
  Star,
  Search,
  X,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Gift,
  ShoppingBag,
  Building2,
  Info,
  LayoutList,
  Grid
} from "lucide-react";

// Certification logos
import bisLogo from "../../assets/logo2.png";
import tbsLogo1 from "../../assets/logo4.png";
import tbsLogo2 from "../../assets/logo3.png";
import kebsLogo from "../../assets/BIS.png";
import mobileBannerImg from "../../assets/RF-img/Realflame Website Pictures (1).png";

function ShopPage() {
  const { category: rawCategory } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Category state (defaults to 'png-stoves')
  const [activeCategoryKey, setActiveCategoryKey] = useState(() => {
    if (!rawCategory) return "png-stoves";
    const lower = rawCategory.toLowerCase();
    if (lower === "lpg" || lower === "lpg-stoves") return "lpg-stoves";
    if (lower === "accessories" || lower === "accessory") return "accessories";
    if (lower === "customization" || lower === "custom") return "customization";
    return "png-stoves";
  });

  // Sync state if rawCategory URL param changes
  useEffect(() => {
    if (!rawCategory) {
      setActiveCategoryKey("png-stoves");
      return;
    }
    const lower = rawCategory.toLowerCase();
    if (lower === "lpg" || lower === "lpg-stoves") setActiveCategoryKey("lpg-stoves");
    else if (lower === "accessories" || lower === "accessory") setActiveCategoryKey("accessories");
    else if (lower === "customization" || lower === "custom") setActiveCategoryKey("customization");
    else setActiveCategoryKey("png-stoves");
  }, [rawCategory]);

  const currentCategoryKey = activeCategoryKey;

  // State
  const [selectedBurnerFilter, setSelectedBurnerFilter] = useState("all");
  const [selectedMaterialFilter, setSelectedMaterialFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("list"); // Default to Glen-style List View!
  const [selectedProductModal, setSelectedProductModal] = useState(null);
  const [activeModalImageIndex, setActiveModalImageIndex] = useState(0);
  const [priceCatalog, setPriceCatalog] = useState({});

  useEffect(() => {
    let isMounted = true;
    getProductPrices().then((prices) => {
      if (isMounted && prices) {
        setPriceCatalog(prices);
      }
    });
    return () => { isMounted = false; };
  }, []);

  const searchInputRef = useRef(null);
  const productListingRef = useRef(null);

  // Flatten raw products into individual items for rich catalogue grid/list
  const allProducts = useMemo(() => {
    const flattened = [];

    productsData.forEach((item) => {
      if (item.variations && item.variations.length > 0) {
        item.variations.forEach((v) => {
          let material = v.material || null;
          if (!material) {
            if (v.title.includes("Glass")) material = "Glass Top";
            else if (v.title.includes("Stainless Steel")) material = "Stainless Steel";
          }

          let burnerCount = v.burnerCount || null;
          if (!burnerCount) {
            if (v.title.includes("Dual") || v.title.includes("2") || v.title.includes("Double")) burnerCount = 2;
            else if (v.title.includes("Three") || v.title.includes("3") || v.title.includes("Triple")) burnerCount = 3;
            else if (v.title.includes("Four") || v.title.includes("4")) burnerCount = 4;
            else if (v.title.includes("Single") || v.title.includes("1")) burnerCount = 1;
          }

          flattened.push({
            id: `${item.id}-${v.sku}`,
            baseId: item.id,
            sku: v.sku,
            title: v.title.startsWith("Realflame") ? v.title : `Realflame ${v.title} ${item.baseTitle}`,
            rawTitle: v.title,
            category: item.baseCategory,
            burnerCount: burnerCount,
            material: material,
            price: v.price || null,
            mrp: v.mrp || null,
            rating: v.rating || 4.5,
            reviews: v.reviews || 12,
            description: v.description || item.baseDescription,
            images: v.images && v.images.length > 0 ? v.images : [item.baseImage],
            mainImage: v.images && v.images.length > 0 ? v.images[0] : item.baseImage,
            specs: v.specs || [],
            freeItems: v.freeItems || [],
            flipkartUrl: v.flipkartUrl || "",
            customization: v.customization || null,
            badge: v.reviews >= 18 ? "BEST SELLER" : v.rating >= 4.6 ? "POPULAR" : "PREMIUM"
          });
        });
      } else {
        flattened.push({
          id: item.id,
          baseId: item.id,
          sku: item.id,
          title: item.baseTitle.startsWith("Realflame") ? item.baseTitle : `Realflame ${item.baseTitle}`,
          rawTitle: item.baseTitle,
          category: item.baseCategory,
          burnerCount: null,
          material: null,
          price: null,
          mrp: null,
          rating: 4.5,
          reviews: 8,
          description: item.baseDescription,
          images: [item.baseImage],
          mainImage: item.baseImage,
          specs: [],
          freeItems: [],
          flipkartUrl: "",
          customization: null,
          badge: "PREMIUM"
        });
      }
    });

    return flattened;
  }, []);

  // Filter products by active category, burner count, material, and search term
  const displayedProducts = useMemo(() => {
    let list = allProducts.filter((p) => p.category === currentCategoryKey);

    // Apply burner filter if applicable (PNG or LPG)
    if (selectedBurnerFilter !== "all") {
      const targetBurners = parseInt(selectedBurnerFilter, 10);
      list = list.filter((p) => p.burnerCount === targetBurners);
    }

    // Apply material filter if applicable (PNG or LPG)
    if (selectedMaterialFilter !== "all") {
      list = list.filter((p) => p.material === selectedMaterialFilter);
    }

    // Apply search query
    if (searchTerm.trim() !== "") {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.sku.toLowerCase().includes(q)
      );
    }

    return list;
  }, [allProducts, currentCategoryKey, selectedBurnerFilter, selectedMaterialFilter, searchTerm]);

  // Handle URL query parameters for search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchVal = params.get("search");
    if (searchVal) setSearchTerm(searchVal);
  }, [location.search]);

  // Scroll to top on category change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSelectedBurnerFilter("all");
    setSelectedMaterialFilter("all");
  }, [currentCategoryKey]);

  // Category navigation click handler
  const handleCategoryNav = (catKey) => {
    setActiveCategoryKey(catKey);
    setSelectedBurnerFilter("all");
    setSelectedMaterialFilter("all");
    setSearchTerm("");

    let urlSlug = "png";
    if (catKey === "lpg-stoves") urlSlug = "lpg";
    else if (catKey === "accessories") urlSlug = "accessories";
    else if (catKey === "customization") urlSlug = "customization";

    navigate(`/products/${urlSlug}`, { replace: true });
  };

  // Material selector click handler
  const handleMaterialSelect = (matVal) => {
    setSelectedMaterialFilter(matVal);
  };

  // Quick view detail modal trigger / detail page navigation
  const openProductDetails = (product) => {
    if (product.category === "png-stoves" || product.baseId === "png-stove" || product.id.includes("GT") || product.id.includes("SS")) {
      navigate(`/product/png-stove?productId=${encodeURIComponent(product.sku || product.id)}`);
    } else {
      setSelectedProductModal(product);
      setActiveModalImageIndex(0);
    }
  };

  const closeProductDetails = () => {
    setSelectedProductModal(null);
  };

  // Buy Now trigger handler
  const handleBuyNow = (product) => {
    if (product.category === "png-stoves" || product.baseId === "png-stove" || product.id?.includes("GT") || product.id?.includes("SS")) {
      navigate(`/product/png-stove?productId=${encodeURIComponent(product.sku || product.id)}`);
    } else if (product.category === "lpg-stoves" || product.baseCategory === "lpg-stoves" || !product.flipkartUrl) {
      navigate(`/contact-us?product=${encodeURIComponent(product.title || product.baseTitle)}`);
    } else if (product.flipkartUrl) {
      window.open(product.flipkartUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/contact-us?product=${encodeURIComponent(product.title || product.baseTitle)}`);
    }
  };

  // SEO setup
  const categoryLabels = {
    "png-stoves": "PNG Gas Stoves",
    "lpg-stoves": "LPG Gas Stoves",
    accessories: "Stove Accessories",
    customization: "Customization Services"
  };

  const currentCategoryLabel = categoryLabels[currentCategoryKey] || "PNG Gas Stoves";
  const pageTitle = `${currentCategoryLabel} | RealFlame Official Store`;
  const pageDesc =
    "Browse RealFlame's premium PNG gas stoves designed for powerful, safe and elegant kitchens. Toughened glass top and stainless steel 2, 3 and 4 burner stoves.";
  const pageCanonical = `https://realflame.co.in/products/${rawCategory || "png"}`;

  const breadcrumbs = [
    { name: "Home", url: "https://realflame.co.in/" },
    { name: "Products", url: "https://realflame.co.in/products" },
    { name: currentCategoryLabel, url: pageCanonical }
  ];

  // Hinglish Reviews dataset for moving marquee ticker
  const customerFeedbackList = [
    {
      id: 1,
      quote: "Stove ka look kaafi premium hai, kitchen mein bahut achha lag raha hai.",
      author: "Rahul Sharma",
      location: "Delhi NCR",
      rating: 5,
      product: "4 Burner Glass Top PNG"
    },
    {
      id: 2,
      quote: "Flame kaafi strong hai aur cooking bhi fast ho rahi hai.",
      author: "Anita Patel",
      location: "Ahmedabad",
      rating: 5,
      product: "3 Burner Stainless Steel PNG"
    },
    {
      id: 3,
      quote: "Glass top clean karna easy hai, overall quality achhi lagi.",
      author: "Priya Singh",
      location: "Mumbai",
      rating: 5,
      product: "2 Burner Glass Top PNG"
    },
    {
      id: 4,
      quote: "4 burner model family ke liye perfect laga, space bhi achha milta hai.",
      author: "Vikram Mehta",
      location: "Pune",
      rating: 5,
      product: "4 Burner Glass Top PNG"
    },
    {
      id: 5,
      quote: "Price ke according finishing aur design kaafi premium hai.",
      author: "Amit Kumar",
      location: "Bengaluru",
      rating: 4,
      product: "3 Burner Glass Top PNG"
    }
  ];

  // Certifications list
  const certificationLogos = [
    { name: "Bureau of Indian Standards (BIS)", badge: "BIS Certified", logo: bisLogo },
    { name: "Tanzania Bureau of Standards (TBS)", badge: "TBS Certified", logo: tbsLogo1 },
    { name: "Kenya Bureau of Standards (KEBS)", badge: "KEBS Certified", logo: kebsLogo },
    { name: "ISO 9001:2015 Quality Management", badge: "ISO 9001", logo: tbsLogo2 }
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={PAGE_SEO.products.keywords}
        canonical={pageCanonical}
        ogImage={PAGE_SEO.products.ogImage}
        structuredData={[breadcrumbSchema(breadcrumbs)]}
      />

      <div className="rf-products-page">
        {/* 1. HERO SECTION */}
        <section className="rf-hero-banner rf-hero-banner--promo">
          {/* Desktop: NO CONVERSION REQUIRED promo image */}
          <div className="rf-hero-promo-wrapper">
            <img
              src={noConversionBanner}
              alt="No Conversion Required"
              className="rf-hero-promo-img"
            />
          </div>
          {/* Mobile: original banner — untouched */}
          <div className="rf-mobile-hero-banner">
            <img src={mobileBannerImg} alt="RealFlame Banner" className="rf-mobile-banner-img" />
          </div>
        </section>

        {/* 2. CATEGORY NAVIGATION TABS */}
        <section className="rf-category-nav-section">
          <div className="rf-max-container">
            <div className="rf-category-tabs">
              <button
                type="button"
                className={`rf-category-tab ${currentCategoryKey === "png-stoves" ? "active" : ""}`}
                onClick={() => handleCategoryNav("png-stoves")}
              >
                <Flame size={18} /> PNG Stoves
              </button>
              <button
                type="button"
                className={`rf-category-tab ${currentCategoryKey === "lpg-stoves" ? "active" : ""}`}
                onClick={() => handleCategoryNav("lpg-stoves")}
              >
                <Flame size={18} /> LPG Stoves
              </button>
              <button
                type="button"
                className={`rf-category-tab ${currentCategoryKey === "accessories" ? "active" : ""}`}
                onClick={() => handleCategoryNav("accessories")}
              >
                <ShoppingBag size={18} /> Accessories
              </button>
              <button
                type="button"
                className={`rf-category-tab ${currentCategoryKey === "customization" ? "active" : ""}`}
                onClick={() => handleCategoryNav("customization")}
              >
                <Building2 size={18} /> Customization
              </button>
            </div>
          </div>
        </section>



        {/* 5. MAIN CATALOGUE PRODUCT LISTING (WITH GLEN STYLE LIST VIEW TOGGLE) */}
        <section className="rf-catalogue-section" ref={productListingRef}>
          <div className="rf-max-container">
            {/* Filter Summary & View Toggle Header */}
            

            {/* Product Display Container */}
            {displayedProducts.length > 0 ? (
              <div className={viewMode === "list" ? "rf-product-list-container" : "rf-product-grid"}>
                {displayedProducts.map((product) => (
                  <ShopCard
                    key={product.id}
                    product={{ ...product, priceCatalog }}
                    viewMode={viewMode}
                    onViewDetails={() => openProductDetails(product)}
                    onBuyNow={() => handleBuyNow(product)}
                  />
                ))}
              </div>
            ) : (
              <div className="rf-empty-state">
                <Info size={40} className="rf-empty-icon" />
                <h3>No matching products found</h3>
                <p>Try clearing your search term or selecting a different burner filter.</p>
                <button
                  type="button"
                  className="rf-reset-filters-btn"
                  onClick={() => {
                    setSelectedBurnerFilter("all");
                    setSelectedMaterialFilter("all");
                    setSearchTerm("");
                  }}
                >
                  Reset All Filters
                </button>
              </div>
            )}
          </div>
        </section>

        {/* 6. RATINGS & TRUST SECTION ("Loved by Home Cooks") */}
        <section className="rf-ratings-trust-section">
          <div className="rf-max-container">
            <div className="rf-section-header">
              <span className="rf-section-tag">VERIFIED RATINGS</span>
              <h2 className="rf-section-title">Loved by Home Cooks</h2>
              <p className="rf-section-subtitle">
                Built for heavy Indian cooking. Rated for safety and performance.
              </p>
            </div>

            <div className="rf-ratings-summary-card">
              <div className="rf-score-block">
                <div className="rf-big-score">4.6</div>
                <div className="rf-score-stars">
                  <Star size={18} fill="#FF7A00" color="#FF7A00" />
                  <Star size={18} fill="#FF7A00" color="#FF7A00" />
                  <Star size={18} fill="#FF7A00" color="#FF7A00" />
                  <Star size={18} fill="#FF7A00" color="#FF7A00" />
                  <Star size={18} fill="url(#halfStarGradient)" color="#FF7A00" />
                </div>
                <div className="rf-score-text">Based on 89 Product Ratings</div>
                <div className="rf-data-note">* Aggregate ratings calculated across Realflame PNG product range</div>
              </div>

              <div className="rf-rating-bars">
                <div className="rf-bar-row">
                  <span className="rf-bar-label">5 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "70%" }} />
                  </div>
                  <span className="rf-bar-percent">70%</span>
                </div>
                <div className="rf-bar-row">
                  <span className="rf-bar-label">4 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "20%" }} />
                  </div>
                  <span className="rf-bar-percent">20%</span>
                </div>
                <div className="rf-bar-row">
                  <span className="rf-bar-label">3 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "6%" }} />
                  </div>
                  <span className="rf-bar-percent">6%</span>
                </div>
                <div className="rf-bar-row">
                  <span className="rf-bar-label">2 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "3%" }} />
                  </div>
                  <span className="rf-bar-percent">3%</span>
                </div>
                <div className="rf-bar-row">
                  <span className="rf-bar-label">1 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "1%" }} />
                  </div>
                  <span className="rf-bar-percent">1%</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 7. MOVING HINGLISH CUSTOMER FEEDBACK CAROUSEL (CSS Marquee) */}
        <section className="rf-feedback-marquee-section">
          <div className="rf-marquee-header">
            <h3 className="rf-feedback-title">What Home Cooks Say About Realflame</h3>
          </div>
          <div className="rf-marquee-container">
            <div className="rf-marquee-track">
              {/* Duplicate review list twice for seamless infinite loop */}
              {[...customerFeedbackList, ...customerFeedbackList].map((rev, idx) => (
                <div className="rf-feedback-card" key={`rev-${rev.id}-${idx}`}>
                  <div className="rf-feedback-stars">
                    {Array.from({ length: rev.rating }).map((_, i) => (
                      <Star key={i} size={14} fill="#FF7A00" color="#FF7A00" />
                    ))}
                  </div>
                  <p className="rf-feedback-quote">"{rev.quote}"</p>
                  <div className="rf-feedback-author">
                    <span className="rf-author-name">{rev.author}</span>
                    <span className="rf-author-loc">{rev.location}</span>
                  </div>
                  <span className="rf-feedback-prod-badge">{rev.product}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 8. CERTIFICATIONS SECTION ("Trusted & Certified") */}
        <section className="rf-certifications-section">
          <div className="rf-max-container">
            <div className="rf-section-header">
              <span className="rf-section-tag">QUALITY & SAFETY</span>
              <h2 className="rf-section-title">Trusted & Certified</h2>
              <p className="rf-section-subtitle">
                Tested against stringent international safety and durability benchmarks.
              </p>
            </div>

            <div className="rf-cert-grid">
              {certificationLogos.map((cert, index) => (
                <div className="rf-cert-card" key={index}>
                  <div className="rf-cert-logo-box">
                    <img src={cert.logo} alt={cert.name} className="rf-cert-logo-img" />
                  </div>
                  <h4 className="rf-cert-name">{cert.name}</h4>
                  <span className="rf-cert-badge-pill">{cert.badge}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>

      {/* 10. PRODUCT QUICK VIEW / DETAILS MODAL */}
      {selectedProductModal && (
        <div className="rf-modal-backdrop" onClick={closeProductDetails}>
          <div
            className="rf-modal-card animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="rf-modal-close-btn"
              onClick={closeProductDetails}
              aria-label="Close details"
            >
              <X size={20} />
            </button>

            <div className="rf-modal-body">
              {/* Left: Image Gallery */}
              <div className="rf-modal-gallery">
                <div className="rf-modal-main-img-box">
                  <img
                    src={
                      selectedProductModal.images[activeModalImageIndex] ||
                      selectedProductModal.mainImage
                    }
                    alt={selectedProductModal.title}
                    className="rf-modal-main-img"
                  />
                </div>

                {selectedProductModal.images.length > 1 && (
                  <div className="rf-modal-thumbnails">
                    {selectedProductModal.images.map((img, idx) => (
                      <div
                        key={idx}
                        className={`rf-thumb-box ${
                          activeModalImageIndex === idx ? "active" : ""
                        }`}
                        onClick={() => setActiveModalImageIndex(idx)}
                      >
                        <img src={img} alt={`Thumbnail ${idx}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Info & Specs */}
              <div className="rf-modal-info">
                <div className="rf-modal-header-badges">
                  <span className="rf-badge rf-badge-best-seller">
                    {selectedProductModal.badge}
                  </span>
                  {selectedProductModal.material && (
                    <span className="rf-chip rf-chip-material">
                      {selectedProductModal.material}
                    </span>
                  )}
                  {selectedProductModal.burnerCount && (
                    <span className="rf-chip rf-chip-burner">
                      {selectedProductModal.burnerCount} Burner
                    </span>
                  )}
                </div>

                <h2 className="rf-modal-title">{selectedProductModal.title}</h2>
                <div className="rf-modal-sku-text">SKU: {selectedProductModal.sku}</div>

                {/* Rating */}
                <div className="rf-card-rating-row">
                  <div className="rf-stars-container">
                    <Star size={14} fill="#FF7A00" color="#FF7A00" />
                    <Star size={14} fill="#FF7A00" color="#FF7A00" />
                    <Star size={14} fill="#FF7A00" color="#FF7A00" />
                    <Star size={14} fill="#FF7A00" color="#FF7A00" />
                    <Star size={14} fill="url(#halfStarGradient)" color="#FF7A00" />
                  </div>
                  <span className="rf-rating-score">
                    {selectedProductModal.rating}
                  </span>
                  <span className="rf-rating-count">
                    ({selectedProductModal.reviews} Ratings)
                  </span>
                </div>

                {/* Price */}
                {selectedProductModal.category === "png-stoves" || selectedProductModal.baseCategory === "png-stoves" || selectedProductModal.baseId === "png-stove" || (selectedProductModal.sku && (selectedProductModal.sku.startsWith("GT") || selectedProductModal.sku.startsWith("SS"))) ? (
                  (selectedProductModal.mrp || typeof selectedProductModal.price === "number") ? (() => {
                    const pricing = getPriceForSku(selectedProductModal.sku, selectedProductModal.mrp || selectedProductModal.price, priceCatalog);
                    const rawMrp = pricing.mrp;
                    const effectivePrice = pricing.effectivePrice;
                    const isDiscountActive = pricing.isDiscountActive;
                    const savingsAmount = pricing.savingsAmount;
                    const discountPercentage = pricing.discountPercentage;
                    return (
                      <div className="rf-modal-price-box flex flex-col gap-1">
                        <div className="flex items-baseline gap-3">
                          <span className="rf-modal-price text-2xl font-bold text-orange-600">
                            ₹{Number(effectivePrice).toLocaleString("en-IN")}
                          </span>
                          {isDiscountActive && savingsAmount > 0 ? (
                            <>
                              <span className="line-through text-sm text-gray-500">
                                MRP ₹{Number(rawMrp).toLocaleString("en-IN")}
                              </span>
                              <span className="badge bg-danger text-white text-xs px-2 py-0.5 rounded">
                                {discountPercentage}% OFF
                              </span>
                            </>
                          ) : null}
                        </div>
                        {isDiscountActive && savingsAmount > 0 ? (
                          <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded w-max border border-green-200">
                            ⚡ Save ₹{Number(savingsAmount).toLocaleString("en-IN")} with Special Discount
                          </span>
                        ) : null}
                      </div>
                    );
                  })() : null
                ) : (
                  <div className="rf-modal-price-box">
                    <span className="rf-modal-price text-xl font-bold text-gray-600">
                      Contact for Quote
                    </span>
                  </div>
                )}

                {/* Description */}
                <p className="rf-modal-desc">{selectedProductModal.description}</p>

                {/* Free Gifts if any */}
                {selectedProductModal.freeItems &&
                  selectedProductModal.freeItems.length > 0 && (
                    <div className="rf-free-gifts-box">
                      <div className="rf-gift-title">
                        <Gift size={16} className="text-orange-500" /> Free Gift Included:
                      </div>
                      <div className="rf-gift-items-list">
                        {selectedProductModal.freeItems.map((gift, gIdx) => (
                          <div className="rf-gift-pill" key={gIdx}>
                            <img src={gift.image} alt={gift.name} />
                            <span>{gift.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Technical Specs Table */}
                {selectedProductModal.specs &&
                  selectedProductModal.specs.length > 0 && (
                    <div className="rf-modal-specs-container">
                      <h4 className="rf-specs-heading">Technical Specifications</h4>
                      <div className="rf-specs-grid">
                        {selectedProductModal.specs.map((sp, sIdx) => (
                          <div className="rf-spec-item" key={sIdx}>
                            <span className="rf-spec-label">{sp.label}:</span>
                            <span className="rf-spec-value">{sp.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Customization Details if Customization Product */}
                {selectedProductModal.customization && (
                  <div className="rf-modal-specs-container">
                    <h4 className="rf-specs-heading">Customization Features</h4>
                    <ul className="rf-custom-features-list">
                      {selectedProductModal.customization.features.map(
                        (feat, fIdx) => (
                          <li key={fIdx}>
                            <CheckCircle2 size={14} className="text-orange-500 shrink-0" />
                            <span>{feat}</span>
                          </li>
                        )
                      )}
                    </ul>
                    {selectedProductModal.customization.note && (
                      <div className="rf-confidential-note">
                        <ShieldCheck size={14} /> {selectedProductModal.customization.note}
                      </div>
                    )}
                  </div>
                )}

                {/* Action CTA */}
                <div className="rf-modal-actions">
                  {selectedProductModal.category === "lpg-stoves" || selectedProductModal.baseCategory === "lpg-stoves" || !selectedProductModal.flipkartUrl ? (
                    <button
                      type="button"
                      className="rf-modal-contact-btn"
                      style={{ width: "100%", padding: "14px 24px", backgroundColor: "#FF7A00", color: "#ffffff", fontWeight: "700", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "1rem" }}
                      onClick={() => {
                        closeProductDetails();
                        navigate(
                          `/contact-us?product=${encodeURIComponent(
                            selectedProductModal.title
                          )}`
                        );
                      }}
                    >
                      CONTACT US
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="rf-modal-buy-btn"
                        onClick={() => handleBuyNow(selectedProductModal)}
                      >
                        BUY NOW ON FLIPKART <ArrowRight size={18} />
                      </button>
                      <button
                        type="button"
                        className="rf-modal-contact-btn"
                        onClick={() => {
                          closeProductDetails();
                          navigate(
                            `/contact-us?product=${encodeURIComponent(
                              selectedProductModal.title
                            )}`
                          );
                        }}
                      >
                        ENQUIRE FOR BULK
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}

export default ShopPage;